'use client';

import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@/layers/presentation/components/ui/Table';
import { useTranslation } from '@/layers/presentation/i18n/client';
import { Button, Link, Skeleton } from '@nextui-org/react';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useMemo } from 'react';

import { useGlobalState } from '@/layers/presentation/context/GlobalStateContext';
import { usePatientsList } from '@/layers/presentation/hooks';
import {
  doesPatientFitProgram,
  getPatientParamValuesExtended,
} from '@/layers/presentation/services/patients';
import logger from '@/layers/support/monitoring/logger';
import { FitStatus, ProgramRequestStatus } from '@/types/modelEnums';
import { PatientExtended, Program } from '@/types/models';
import { useRouter } from 'next/navigation';

const LOG_MODULE = 'FE [Presentation / Components / PatientsList]';

interface PatientsListProps {
  type: 'match' | 'view';
  filters: Record<string, any>;
  program?: Program;
}

export default function PatientsList({ type, filters, program }: PatientsListProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const { patientParamsDictionary } = useGlobalState();
  const { patients, loadingPatients, error } = usePatientsList(filters);

  const columns = useMemo(() => {
    return [
      { key: 'created_at', label: t('components:patientsList.columns.createdAt') },
      // { key: 'info', label: t('components:patientsList.columns.info') },
      { key: 'gender', label: 'GENDER' },
      { key: 'age', label: 'AGE' },
      { key: 'c3_cancer_type', label: t('components:patientsList.columns.cancerType') },
      ...(type === 'match'
        ? []
        : [{ key: 'program', label: t('components:patientsList.columns.program') }]),
      { key: 'actions', label: t('components:patientsList.columns.actions') },
    ];
  }, [t, type]);

  const filteredPatients: PatientExtended[] = useMemo(() => {
    let filtered = patients.map(patient => ({ ...patient })); // Create a shallow copy

    if (type === 'match') {
      filtered = filtered.filter(
        patient =>
          !patient.programRequests?.some(app => app.status === ProgramRequestStatus.Approved),
      );

      if (program) {
        filtered = filtered
          .map(patient => {
            const fitStatus = doesPatientFitProgram(patient.params, program.patient_params);
            return { ...patient, fitStatus };
          })
          .filter(
            patient =>
              patient.fitStatus === FitStatus.FULLY_FIT ||
              patient.fitStatus === FitStatus.PARTIALLY_FIT,
          );
      }
    }

    return filtered;
  }, [patients, type, program]);

  const renderCell = useCallback(
    (patient: PatientExtended, columnKey: React.Key): React.ReactNode => {
      const cellValue = patient[columnKey as keyof PatientExtended];

      switch (columnKey) {
        case 'created_at':
          if (cellValue) {
            const date = new Date(cellValue as string);
            return format(date, 'yyyy-MM-dd');
          }
          return t('components:patientsList.notAvailable');

        case 'info':
          const { gender, age } = patient.personal_info || {};
          let label = '';
          if (gender && age) label = `${gender}, ${age}`;
          else label = `${gender} ${age}`;

          return <Link href={`/provider/patients/${patient.id}`}>{label}</Link>;

        case 'gender':
        case 'age':
        case 'c3_cancer_type':
          const paramValuesExtended = getPatientParamValuesExtended(
            patientParamsDictionary,
            patient.params,
          );
          const paramValueExtended = Object.values(paramValuesExtended).find(
            p => p.param.slug === columnKey,
          );

          if (paramValueExtended) return paramValueExtended.values.map(v => v.label).join(', ');
          else return t('components:patientsList.notAvailable');

        case 'program':
          const programRequests = patient.programRequests || [];
          const latestRequest = programRequests[programRequests.length - 1];

          if (programRequests.length > 1) {
            return (
              <>
                <div>Last: {latestRequest.program?.name}</div>
                <div>Total: {programRequests.length}</div>
              </>
            );
          } else if (programRequests.length === 1) {
            return latestRequest.program?.name || t('components:patientsList.notSelected');
          }
          return t('components:patientsList.notSelected');

        case 'fitStatus':
          return patient.fitStatus
            ? t(`components:patientsList.${patient.fitStatus}`)
            : t('components:patientsList.notAvailable');

        case 'actions':
          if (type === 'match' && program) {
            const programRequest = patient.programRequests?.find(
              programRequest => programRequest.program_id === program.id,
            );
            if (programRequest) {
              return (
                <div className="flex flex-row gap-2">
                  <Button
                    color="default"
                    variant="bordered"
                    radius="sm"
                    size="sm"
                    className="border-1"
                    onClick={() =>
                      router.push(`/provider/programs/${program.id}/request/${patient.id}`)
                    }
                  >
                    {t('components:patientsList.viewRequest')}
                  </Button>
                  <Link href={`/provider/patients/${patient.id}`}>
                    <Button variant="bordered" radius="sm" size="sm">
                      {t('components:patientsList.viewPatient')}
                    </Button>
                  </Link>
                </div>
              );
            } else if (patient.fitStatus === FitStatus.FULLY_FIT) {
              return (
                <div className="flex flex-row gap-2">
                  <Button
                    color="primary"
                    radius="sm"
                    size="sm"
                    onClick={() =>
                      router.push(`/provider/programs/${program.id}/request/${patient.id}`)
                    }
                  >
                    {t('components:patientsList.request')}
                  </Button>
                  <Link href={`/provider/patients/${patient.id}`}>
                    <Button variant="bordered" radius="sm" size="sm">
                      {t('components:patientsList.viewPatient')}
                    </Button>
                  </Link>
                </div>
              );
            } else {
              return (
                <Link href={`/provider/patients/${patient.id}`}>
                  <Button variant="bordered" radius="sm" size="sm">
                    {t('components:patientsList.edit')}
                  </Button>
                </Link>
              );
            }
          }
          return (
            <Button
              color="default"
              variant="bordered"
              radius="sm"
              size="sm"
              as={Link}
              className="border-1"
              href={`/provider/patients/${patient.id}`}
            >
              {t('components:patientsList.view')}
            </Button>
          );

        default:
          return cellValue != null ? String(cellValue) : null;
      }
    },
    [patientParamsDictionary, program, router, type, t],
  );

  useEffect(() => {
    logger.debug(`${LOG_MODULE} Rendering PatientsList`, {
      filters,
      program,
      patientParamsDictionary,
      patients,
      loadingPatients,
      error,
      filteredPatients,
    });
  }, [
    filters,
    program,
    patientParamsDictionary,
    patients,
    loadingPatients,
    error,
    filteredPatients,
  ]);

  return (
    <Table aria-label={t('components:patientsList.ariaLabel')} selectionBehavior="replace">
      <TableHeader columns={columns}>
        {column => <TableColumn key={column.key}>{column.label}</TableColumn>}
      </TableHeader>
      <TableBody items={filteredPatients} emptyContent={t('components:patientsList.noPatients')}>
        {loadingPatients ? (
          <TableRow>
            {columns.map(column => (
              <TableCell key={column.key}>
                <Skeleton className="w-full h-8 rounded-md" />
              </TableCell>
            ))}
          </TableRow>
        ) : (
          filteredPatients.map(patient => (
            <TableRow key={patient.id}>
              {columnKey => (
                <TableCell
                  className={
                    ['created_at', 'info'].includes(columnKey as string) ? 'whitespace-nowrap' : ''
                  }
                >
                  {renderCell(patient, columnKey)}
                </TableCell>
              )}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
